import React, { useState, useEffect, useCallback } from 'react';

// EOL Scans — per-associate IDRT (RSR Support) activity for a selected day.
// Reads the EOL_SCANS_<date> cache populated every ~5 min by eol-scans-sync.sh.
// Columns: Associate | Hours | Jobs | CPLH (= Jobs / Hours).

export default function EOLScans({ api, site }) {
  const [date, setDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shift, setShift] = useState('day');
  const [cages, setCages] = useState(null);   // CAGES_OLM1 (from the IDRT Jar Jar userscript)
  const [pallets, setPallets] = useState(null);   // RODEO_PALLETS_OLM1 (pallet count by destination)
  const [rsr, setRsr] = useState(null);           // RSR_SCANS_<date> (hourly backend sync)
  const [rsrCsx, setRsrCsx] = useState('');        // CSX search in the RSR scan index
  const [onsite, setOnsite] = useState({});        // empId -> {sosPunch,lunchOut,lunchIn,endOut}
  const [expandedEmp, setExpandedEmp] = useState(null);  // associate timeline expanded
  const [quarter, setQuarter] = useState('all');   // 'all' | 'q1'..'q4' — shift-quarter rate filter

  useEffect(() => {
    const pst = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const h = pst.getHours();
    // Night shift is keyed by its START date; before 06:00 we're still in
    // yesterday's night shift.
    const d = new Date(pst);
    if (h < 6) d.setDate(d.getDate() - 1);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    setShift(h >= 8 && h < 19 ? 'day' : 'night');
  }, []);

  const load = useCallback(() => {
    if (!date) return;
    setLoading(true);
    fetch(`${api}/api/fclm/cache?site=${site}&date=EOL_SCANS_${date}&shift_type=${shift}`)
      .then(r => r.json())
      .then(d => setData(d && Array.isArray(d.rows) ? d : null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [api, site, date, shift]);

  useEffect(() => { load(); }, [load]);
  // Refresh every 60s so the view tracks the 5-min sync.
  useEffect(() => { if (!date) return; const t = setInterval(load, 60000); return () => clearInterval(t); }, [load, date]);

  // RSR Support per-associate CSX scan activity (RSR_SCANS_<date>, hourly sync).
  const loadRsr = useCallback(() => {
    if (!date) return;
    fetch(`${api}/api/fclm/cache?site=${site}&date=RSR_SCANS_${date}&shift_type=${shift}`)
      .then(r => r.json())
      .then(d => setRsr(d && Array.isArray(d.byAssoc) ? d : null))
      .catch(() => setRsr(null));
  }, [api, site, date, shift]);
  useEffect(() => { loadRsr(); const t = setInterval(loadRsr, 120000); return () => clearInterval(t); }, [loadRsr]);

  // Roll Call onsite punches (SOS/lunch/endOut per empId) for the associate
  // activity timeline. Keyed per date/shift like RSR.
  useEffect(() => {
    if (!date) return;
    const pull = () => fetch(`${api}/api/fclm/cache?site=${site}&date=ONSITE_${site}_${date}&shift_type=${shift}`)
      .then(r => r.json())
      .then(d => { const m = {}; ((d && d.onsite) || []).forEach(o => { if (o.empId) m[o.empId] = o; }); setOnsite(m); })
      .catch(() => setOnsite({}));
    pull(); const t = setInterval(pull, 120000); return () => clearInterval(t);
  }, [api, site, date, shift]);
  // current + recent date/shift keys (each shift is its own cache key and they
  // persist, so a CSX scanned on a prior shift is still findable).
  const [rsrHistory, setRsrHistory] = useState({});   // csx -> [{name, empId, time, action, date, shift}]
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    const base = new Date(date + 'T12:00:00');
    const keys = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(base); d.setDate(d.getDate() - i);
      const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      keys.push([ds, 'day'], [ds, 'night']);
    }
    Promise.all(keys.map(([ds, sh]) =>
      fetch(`${api}/api/fclm/cache?site=${site}&date=RSR_SCANS_${ds}&shift_type=${sh}`)
        .then(r => r.json()).then(d => ({ ds, sh, d })).catch(() => null)
    )).then(results => {
      if (cancelled) return;
      const merged = {};
      results.forEach(res => {
        if (!res || !res.d || !res.d.csxIndex) return;
        Object.keys(res.d.csxIndex).forEach(csx => {
          const arr = merged[csx] || (merged[csx] = []);
          res.d.csxIndex[csx].forEach(e => arr.push({ ...e, date: res.ds, shift: res.sh }));
        });
      });
      setRsrHistory(merged);
    });
    return () => { cancelled = true; };
  }, [api, site, date]);

  // Cages from Rodeo (RODEO_CAGES_<site>_<n>, chunked). Each cage ->
  // [[csx, destination, qty], ...] with destinations inline. Replaces the old
  // FC Research scanner feed. Global "right now" state.
  const CAGE_MAX = 660;
  const loadCages = useCallback(async () => {
    try {
      const first = await fetch(`${api}/api/fclm/cache?site=${site}&date=RODEO_CAGES_${site}_0&shift_type=data`).then(r => r.json());
      if (!first || !first.cages) { setCages(null); return; }
      let map = { ...first.cages };
      const count = first.chunkCount || 1;
      for (let i = 1; i < count; i++) {
        try {
          const c = await fetch(`${api}/api/fclm/cache?site=${site}&date=RODEO_CAGES_${site}_${i}&shift_type=data`).then(r => r.json());
          if (c && c.cages) map = { ...map, ...c.cages };
        } catch (e) { /* skip */ }
      }
      setCages({ map, generatedAt: first.generatedAt || null });
    } catch (e) { setCages(null); }
  }, [api, site]);
  useEffect(() => { loadCages(); const t = setInterval(loadCages, 60000); return () => clearInterval(t); }, [loadCages]);

  // Pallets from Rodeo (RODEO_PALLETS_<site>): pallet + case counts by
  // destination, grouped from VIRTUALLY_LABELED_CASE/PALLET rows in the userscript.
  const loadPallets = useCallback(async () => {
    try {
      const d = await fetch(`${api}/api/fclm/cache?site=${site}&date=RODEO_PALLETS_${site}&shift_type=data`).then(r => r.json());
      setPallets(d && Array.isArray(d.byDest) ? d : null);
    } catch (e) { setPallets(null); }
  }, [api, site]);
  useEffect(() => { loadPallets(); const t = setInterval(loadPallets, 60000); return () => clearInterval(t); }, [loadPallets]);

  // ARC % (weekly manual list from FALLIN) and per-ARC live load state from the
  // VRID forecaster (VRID_FORECAST_<site>: inProgress/scheduled, rebuilt fresh
  // each ~5-min pull from the current SSP OB dock view — accurate "now", unlike
  // the cumulative OB_DOOR_MAP which over-reports stale doors).
  const [arcPct, setArcPct] = useState({});     // ARC -> pct
  const [arcLoad, setArcLoad] = useState({});   // ARC -> {inProgress, scheduled}
  useEffect(() => {
    const pac = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
    const sunday = new Date(pac); sunday.setDate(pac.getDate() - pac.getDay());
    const ws = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
    const pull = () => {
      fetch(`${api}/api/fclm/cache?site=${site}&date=VRID_ARC_PCT_${ws}&shift_type=data`)
        .then(r => r.json())
        .then(d => { const m = {}; (Array.isArray(d?.rows) ? d.rows : []).forEach(r => { if (r.arc) m[r.arc] = r.pct; }); setArcPct(m); })
        .catch(() => setArcPct({}));
      fetch(`${api}/api/fclm/cache?site=${site}&date=VRID_FORECAST_${site}&shift_type=data`)
        .then(r => r.json())
        .then(d => {
          const out = {};
          const arcs = (d && d.arcs) || {};
          Object.keys(arcs).forEach(a => { out[a] = { inProgress: arcs[a].inProgress || 0, scheduled: arcs[a].scheduled || 0 }; });
          setArcLoad(out);
        })
        .catch(() => setArcLoad({}));
    };
    pull(); const t = setInterval(pull, 60000); return () => clearInterval(t);
  }, [api, site]);

  // Who scanned a CSX THIS SHIFT (name + empId + time), from the RSR history.
  // rsrHistory entries are tagged with {date, shift}; scope to the viewed shift
  // so the miss-scan reflects the current fill, not an older scan of that CSX.
  const scanInfo = useCallback((csx) => {
    const hits = rsrHistory[csx];
    if (!hits || !hits.length) return null;
    const thisShift = hits.filter(h => h.date === date && h.shift === shift);
    const pool = thisShift.length ? thisShift : [];   // this-shift only for miss attribution
    if (!pool.length) return null;
    const latest = [...pool].sort((a, b) => String(b.time).localeCompare(String(a.time)))[0];
    // Time -> HH:MM:SS (strip "YYYY/MM/DD " prefix and " PDT/PST" suffix).
    const clk = latest.time ? String(latest.time).replace(/^\S+\s/, '').replace(/\s*(PDT|PST)$/, '') : '';
    return { name: latest.name || null, empId: latest.empId || null, time: clk };
  }, [rsrHistory, date, shift]);

  // Per-cage destination analysis from the Rodeo cages. Items: [csx, dest, qty, dwellHrs].
  // Rules:
  //  - A cage with <4 items counts as EMPTY (not loaded).
  //  - "Ghost" items: when the minority (non-primary) items number <=3, drop any
  //    minority item dwelling > (max dwell of the primary group) + 1h — it's an
  //    un-scanned-out carton from a previous fill, not a real mix. Recompute
  //    mixed/OK/miss on the ghost-adjusted set.
  const MIN_LOADED = 4;        // <4 items => empty
  const GHOST_GAP_HRS = 1;     // minority item older than max-primary by >1h => ghost
  const cageAnalysis = React.useMemo(() => {
    if (!cages?.map) return null;
    const map = cages.map;
    const cageIds = Object.keys(map);
    const perCage = [];
    const cagesPerArc = {};
    const cartonsPerArc = {};
    let loaded = 0, mixed = 0, ghostsDropped = 0;
    cageIds.forEach(cage => {
      const items = map[cage] || [];
      if (items.length < MIN_LOADED) return;   // <4 items => empty, skip
      loaded++;

      // Count by dest to find the primary (bulk) destination.
      const destCount = {};
      items.forEach(it => { if (it[1]) destCount[it[1]] = (destCount[it[1]] || 0) + 1; });
      const primaryDest = Object.keys(destCount).sort((a, b) => destCount[b] - destCount[a])[0] || null;
      // Max dwell among the primary-group items.
      let maxPrimaryDwell = 0;
      items.forEach(it => { if (it[1] === primaryDest && typeof it[3] === 'number') maxPrimaryDwell = Math.max(maxPrimaryDwell, it[3]); });

      // Minority items (not primary dest).
      const minority = items.filter(it => it[1] && it[1] !== primaryDest);
      // Apply ghost drop only when minority is small (<=3).
      let ghosts = [];
      let kept = items;
      if (minority.length > 0 && minority.length <= 3) {
        ghosts = minority.filter(it => typeof it[3] === 'number' && it[3] > maxPrimaryDwell + GHOST_GAP_HRS);
        if (ghosts.length) {
          const ghostCsx = new Set(ghosts.map(g => g[0]));
          kept = items.filter(it => !ghostCsx.has(it[0]));
          ghostsDropped += ghosts.length;
        }
      }

      // Recompute dests on the kept (ghost-adjusted) set.
      const dests = {}, destCsx = {};
      let cartons = 0;
      kept.forEach(it => {
        const csx = it[0], dest = it[1], qty = it[2] || 0;
        if (!dest) return;
        dests[dest] = (dests[dest] || 0) + 1;
        (destCsx[dest] = destCsx[dest] || []).push(csx);
        cartons += qty;
        cartonsPerArc[dest] = (cartonsPerArc[dest] || 0) + qty;
      });
      const destKeys = Object.keys(dests);
      const keptPrimary = destKeys.sort((a, b) => dests[b] - dests[a])[0] || null;
      if (destKeys.length > 1) mixed++;
      if (keptPrimary) cagesPerArc[keptPrimary] = (cagesPerArc[keptPrimary] || 0) + 1;

      let miss = null;
      if (destKeys.length > 1) {
        const minorDests = destKeys.filter(d => dests[d] === 1);
        const outsidePrimary = destKeys.filter(d => d !== keptPrimary).reduce((s, d) => s + dests[d], 0);
        if (outsidePrimary === 1 && minorDests.length === 1) {
          const missCsx = destCsx[minorDests[0]][0];
          const info = scanInfo(missCsx);
          miss = { csx: missCsx, dest: minorDests[0], name: info ? info.name : null, time: info ? info.time : null, empId: info ? info.empId : null };
        }
      }
      perCage.push({ cage, childCount: kept.length, dests, cartons, primaryDest: keptPrimary, destCount: destKeys.length, miss, ghosts: ghosts.length });
    });
    perCage.sort((a, b) => ((a.destCount > 1) === (b.destCount > 1) ? b.childCount - a.childCount : (a.destCount > 1 ? -1 : 1)));
    const arcRows = Object.keys(cartonsPerArc).map(d => ({ dest: d, cages: cagesPerArc[d] || 0, cartons: cartonsPerArc[d] }))
      .filter(r => r.cages > 0).sort((a, b) => b.cartons - a.cartons);

    // Miss-scans this shift, grouped by associate (single-miss cages whose
    // outlier was scanned by a named RSR associate THIS shift — scanInfo already
    // scopes to the viewed shift, so unresolved/other-shift misses have no name).
    const byAssoc = {};
    perCage.forEach(c => {
      if (c.miss && c.miss.name) {
        const a = byAssoc[c.miss.name] || (byAssoc[c.miss.name] = { name: c.miss.name, empId: c.miss.empId, scans: [] });
        a.scans.push({ cage: c.cage, csx: c.miss.csx, dest: c.miss.dest, time: c.miss.time });
      }
    });
    const missByAssoc = Object.values(byAssoc).map(a => ({
      ...a, count: a.scans.length,
      scans: a.scans.sort((x, y) => String(y.time).localeCompare(String(x.time))),
    })).sort((a, b) => b.count - a.count);

    return { loaded, empty: Math.max(0, CAGE_MAX - loaded), mixed, ghostsDropped, perCage, arcRows, missByAssoc };
  }, [cages, scanInfo]);




  const rawRows = data?.rows || [];
  const summary = data?.summary || null;

  // Shift-quarter rate filter. Quarters are bounded by the scheduled break
  // windows (same fixed windows used by breakGaps/timeline):
  //   Day   (08:00–18:30): break 10:00, lunch 12:45, break 16:00
  //     Q1 SOS→Break1 = hrs 8,9 · Q2 Break1→Lunch = 10,11,12 ·
  //     Q3 Lunch→Break2 = 13,14,15 · Q4 Break2→EOS = 16,17,18
  //   Night (19:00–05:30): break 21:00, lunch 23:45, break 03:00
  //     Q1 = 19,20 · Q2 = 21,22,23 · Q3 = 0,1,2 · Q4 = 3,4
  // When a quarter is selected we recompute each row's hours/jobs/cplh from the
  // per-hour byHour buckets (real FCLM paid hours) and keep only AAs active in
  // that window. 'all' is untouched (authoritative day totals from the sync).
  const QUARTERS = {
    day:   { q1: ['8', '9'], q2: ['10', '11', '12'], q3: ['13', '14', '15'], q4: ['16', '17', '18'] },
    night: { q1: ['19', '20'], q2: ['21', '22', '23'], q3: ['0', '1', '2'], q4: ['3', '4'] },
  };
  const QUARTER_LABELS = { q1: 'Q1 · SOS→Break 1', q2: 'Q2 · Break 1→Lunch', q3: 'Q3 · Lunch→Break 2', q4: 'Q4 · Break 2→EOS' };
  const hasByHour = rawRows.some(r => r && r.byHour && Object.keys(r.byHour).length);
  const quarterRows = React.useMemo(() => {
    if (quarter === 'all') return rawRows;
    const hrsList = (QUARTERS[shift] || QUARTERS.day)[quarter] || [];
    const out = [];
    rawRows.forEach(r => {
      const bh = r.byHour || {};
      let h = 0, j = 0;
      hrsList.forEach(H => { if (bh[H]) { h += bh[H].h || 0; j += bh[H].j || 0; } });
      if (h <= 0 && j <= 0) return;   // not active in this quarter
      h = Math.round(h * 100) / 100;
      out.push({ ...r, hours: h, jobs: j, cplh: h > 0 ? Math.round((j / h) * 10) / 10 : 0 });
    });
    return out;
  }, [rawRows, quarter, shift]);

  // Sort by CPLH descending (most productive first).
  const rows = [...quarterRows].sort((a, b) => (b.cplh || 0) - (a.cplh || 0));
  // "Total (without managers)": only count jobs/hours for associates who logged
  // hours > 0 (people with 0 hours are scanning as managers, not on the IDRT clock).
  const worked = rows.filter(r => (r.hours || 0) > 0);
  const wmHours = worked.reduce((s, r) => s + (r.hours || 0), 0);
  const wmJobs = worked.reduce((s, r) => s + (r.jobs || 0), 0);
  const wmCplh = wmHours > 0 ? Math.round((wmJobs / wmHours) * 10) / 10 : 0;
  // Condensed styles.
  const cell = { border: '1px solid #ccc', padding: '1px 6px', fontSize: '0.68rem', lineHeight: 1.1 };
  const hdr = { ...cell, background: '#232f3e', color: '#fff', textAlign: 'left', fontWeight: 600 };
  const num = { ...cell, textAlign: 'right' };
  const totalStyle = { ...cell, fontWeight: 700, background: '#f1f8e9' };
  const totalNum = { ...num, fontWeight: 700, background: '#f1f8e9' };

  // empId -> {first,last} scan time (HH:MM:SS) from the RSR scan sync, to show
  // First/Last columns in the main table. Strip the leading "YYYY/MM/DD " and
  // trailing " PDT/PST" so just the clock time shows.
  const clock = (t) => (t ? String(t).replace(/^\S+\s/, '').replace(/\s*(PDT|PST)$/, '') : '');
  const scanTimes = React.useMemo(() => {
    const m = {};
    if (rsr && Array.isArray(rsr.byAssoc)) rsr.byAssoc.forEach(a => { m[a.empId] = { first: clock(a.firstScan), last: clock(a.lastScan) }; });
    return m;
  }, [rsr]);

  // "Breaks exceeding 5 min" per associate: count scan-to-scan gaps > 5 min,
  // EXCLUDING gaps that fall within scheduled break/lunch windows unless the gap
  // extends >5 min beyond a window's start or end (grace rule). Windows (fixed):
  //   Day:   break 10:00-10:15, 16:00-16:15 ; lunch 12:45-13:15
  //   Night: break 21:00-21:15, 03:00-03:15 ; lunch 23:45-00:15
  // Pure client-side math on the already-loaded RSR scans — no extra fetches.
  const breakGaps = React.useMemo(() => {
    const out = {};
    if (!rsr || !rsr.csxIndex) return out;
    // Parse "YYYY/MM/DD HH:MM:SS PDT" -> epoch ms (local; all scans share zone so
    // diffs are correct) and minutes-since-midnight for window checks.
    const parse = (t) => {
      const m = String(t || '').match(/(\d{4})\/(\d{2})\/(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
      if (!m) return null;
      const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
      return { ms: d.getTime(), mins: +m[4] * 60 + +m[5] + (+m[6]) / 60 };
    };
    // Windows as [startMin, endMin] in minutes-since-midnight; night lunch wraps.
    const win = shift === 'day'
      ? [[600, 615], [960, 975], [765, 795]]                  // 10:00,16:00 breaks; 12:45-13:15 lunch
      : [[1260, 1275], [180, 195], [1425, 1440], [0, 15]];     // 21:00,03:00 breaks; 23:45-24:00 + 00:00-00:15 lunch
    const GAP_MIN = 5, GRACE = 5;
    // A gap [aMin,bMin] "counts" against a window [s,e] only if it extends
    // >GRACE before s OR >GRACE after e; a gap sitting within ±GRACE is excused.
    const excusedBy = (aMin, bMin, s, e) => {
      // overlaps the window at all?
      if (bMin <= s || aMin >= e) return false;        // no overlap -> not excused by this window
      const extendsBefore = aMin < s - GRACE;
      const extendsAfter = bMin > e + GRACE;
      return !(extendsBefore || extendsAfter);          // excused if it does NOT extend >grace either side
    };

    // Gather per-empId scan time list from csxIndex.
    const byEmp = {};
    Object.values(rsr.csxIndex).forEach(arr => arr.forEach(e => {
      if (!e.empId || !e.time) return;
      (byEmp[e.empId] = byEmp[e.empId] || []).push(e.time);
    }));

    Object.keys(byEmp).forEach(emp => {
      const pts = byEmp[emp].map(parse).filter(Boolean).sort((a, b) => a.ms - b.ms);
      let count = 0;
      for (let i = 1; i < pts.length; i++) {
        const gapMs = pts[i].ms - pts[i - 1].ms;
        if (gapMs <= GAP_MIN * 60000) continue;
        const aMin = pts[i - 1].mins, bMin = pts[i].mins;
        // Excused if the gap is "within" any single break/lunch window per grace rule.
        const excused = win.some(([s, e]) => excusedBy(aMin, bMin, s, e));
        if (!excused) count++;
      }
      out[emp] = count;
    });
    return out;
  }, [rsr, shift]);

  // Per-empId sorted scan epochs (ms) for the activity timeline.
  const scanEpochsByEmp = React.useMemo(() => {
    const out = {};
    if (!rsr || !rsr.csxIndex) return out;
    const parseMs = (t) => { const m = String(t || '').match(/(\d{4})\/(\d{2})\/(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/); return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]).getTime() : null; };
    Object.values(rsr.csxIndex).forEach(arr => arr.forEach(e => {
      if (!e.empId || !e.time) return; const ms = parseMs(e.time); if (ms != null) (out[e.empId] = out[e.empId] || []).push(ms);
    }));
    Object.keys(out).forEach(k => out[k].sort((a, b) => a - b));
    return out;
  }, [rsr]);

  // Build an activity timeline for one associate: segments (scanning / idle /
  // break) from SOS -> now (or endOut), plus markers. Punches are HH:MM clock
  // times on the plan date; scans are full datetimes.
  const buildTimeline = React.useCallback((empId) => {
    const scans = scanEpochsByEmp[empId] || [];
    const o = onsite[empId] || {};
    if (!date) return null;
    // No per-scan data for this associate (they have jobs in the rollup but no
    // rows in the RSR scan index — the scan scrape didn't capture them). Report
    // it rather than drawing a misleading all-idle bar.
    if (scans.length === 0) return { noScans: true };
    const [Y, M, D] = date.split('-').map(Number);
    const atClock = (hhmm) => { if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null; const [h, mi] = hhmm.split(':').map(Number); let dt = new Date(Y, M - 1, D, h, mi, 0).getTime(); if (shift === 'night' && h < 12) dt += 86400000; return dt; };
    const sos = atClock(o.sosPunch) || (scans.length ? scans[0] : null);
    if (sos == null) return null;
    const endOut = atClock(o.endOut);
    const end = endOut || Date.now();
    const lunchOut = atClock(o.lunchOut), lunchIn = atClock(o.lunchIn);

    // Scheduled break windows for the shift (same fixed windows as breakGaps).
    const bw = (hStart, mStart, hEnd, mEnd) => { let s = new Date(Y, M - 1, D, hStart, mStart).getTime(), e = new Date(Y, M - 1, D, hEnd, mEnd).getTime(); if (shift === 'night') { if (hStart < 12) s += 86400000; if (hEnd < 12) e += 86400000; } return [s, e]; };
    const breaks = shift === 'day' ? [bw(10, 0, 10, 15), bw(16, 0, 16, 15), bw(12, 45, 13, 15)] : [bw(21, 0, 21, 15), bw(3, 0, 3, 15), bw(23, 45, 24, 15)];
    if (lunchOut && lunchIn) breaks.push([lunchOut, lunchIn]);
    const inBreak = (ms) => breaks.some(([s, e]) => ms >= s && ms < e);

    // Walk SOS->end in 1-min steps, classify: break > scanning (scan within
    // ±5min) > idle. Coalesce consecutive same-type minutes into segments.
    const GAP = 5 * 60000;
    const firstScan = scans[0], lastScan = scans[scans.length - 1];
    // "scan" only within [firstScan, lastScan] and near a scan — the ±5min
    // window must NOT bleed before the first scan or after the last (that
    // pre/post time is idle, not scanning).
    const near = (ms) => {
      if (ms < firstScan || ms > lastScan) return false;
      for (let i = 0; i < scans.length; i++) { if (Math.abs(scans[i] - ms) <= GAP) return true; if (scans[i] > ms + GAP) break; }
      return false;
    };
    const segs = []; let cur = null;
    for (let t = sos; t < end; t += 60000) {
      const type = inBreak(t) ? 'break' : (near(t) ? 'scan' : 'idle');
      if (cur && cur.type === type) cur.endMs = t + 60000; else { cur = { startMs: t, endMs: t + 60000, type }; segs.push(cur); }
    }
    const markers = [];
    const mk = (ms, label) => { if (ms != null && ms >= sos && ms <= end) markers.push({ ms, label }); };
    mk(sos, 'SOS'); mk(scans[0], '1st scan'); mk(lunchOut, 'Lunch out'); mk(lunchIn, 'Lunch in'); mk(scans[scans.length - 1], 'Last scan'); if (endOut) mk(endOut, 'Clock out');
    return { sos, end, segs, markers, scanCount: scans.length, idleMin: segs.filter(s => s.type === 'idle').reduce((a, s) => a + (s.endMs - s.startMs) / 60000, 0) };
  }, [scanEpochsByEmp, onsite, date, shift]);

  return (
    <div>
      <h2 className="section-title">IDRT</h2>
      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>Date: <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <label style={{ fontSize: '0.75rem' }}>Shift:{' '}
          <select value={shift} onChange={e => setShift(e.target.value)} style={{ fontSize: '0.75rem' }}>
            <option value="day">Day</option><option value="night">Night</option>
          </select>
        </label>
        {summary && (
          <span style={{ fontSize: '0.75rem', color: '#444' }}>
            <strong>{worked.length}</strong> associates · <strong>{Math.round(wmHours).toLocaleString()}</strong> hrs ·
            {' '}<strong>{wmJobs.toLocaleString()}</strong> jobs · CPLH <strong>{wmCplh}</strong>
            {quarter !== 'all' && <span style={{ color: '#1565c0', marginLeft: 6 }}>· {QUARTER_LABELS[quarter]}</span>}
          </span>
        )}
        {data?.fetched_at && <span style={{ fontSize: '0.65rem', color: '#888' }}>Updated {new Date(data.fetched_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · auto-refresh 60s</span>}
      </div>

      {/* Shift-quarter rate selector. Recomputes hours/jobs/CPLH from per-hour
          FCLM buckets and shows only AAs active during the selected quarter. */}
      <div className="card" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, padding: '6px 10px' }}>
        <span style={{ fontSize: '0.7rem', color: '#555', marginRight: 2 }}>Shift quarter:</span>
        {[['all', 'All Day'], ['q1', 'Q1'], ['q2', 'Q2'], ['q3', 'Q3'], ['q4', 'Q4']].map(([q, lbl]) => (
          <button key={q} onClick={() => setQuarter(q)} disabled={q !== 'all' && !hasByHour}
            title={q !== 'all' && !hasByHour ? 'Per-hour data not available yet (backend sync pending)' : (QUARTER_LABELS[q] || 'Whole shift')}
            style={{ fontSize: '0.68rem', padding: '3px 10px', borderRadius: 3, border: '1px solid #ccc',
              cursor: (q !== 'all' && !hasByHour) ? 'not-allowed' : 'pointer', opacity: (q !== 'all' && !hasByHour) ? 0.45 : 1,
              background: quarter === q ? '#232f3e' : '#fff', color: quarter === q ? '#fff' : '#232f3e', fontWeight: quarter === q ? 700 : 400 }}>
            {lbl}
          </button>
        ))}
        {quarter !== 'all' && <span style={{ fontSize: '0.66rem', color: '#1565c0' }}>{QUARTER_LABELS[quarter]} · {worked.length} active AA{worked.length === 1 ? '' : 's'}</span>}
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        {loading && !data ? <p style={{ fontSize: '0.75rem', color: '#888' }}>Loading…</p> : (
          rows.length === 0 ? (
            <p style={{ fontSize: '0.8rem', color: '#888' }}>No IDRT activity cached for {date || 'this date'} yet. Data syncs every ~5 minutes.</p>
          ) : (
            <table style={{ borderCollapse: 'collapse', width: 'auto' }}>
              <thead>
                <tr>
                  <th style={hdr}>Associate</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Hours</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Jobs</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>CPLH</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>First</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Last</th>
                  <th style={{ ...hdr, textAlign: 'right' }}>Breaks &gt;5min</th>
                </tr>
              </thead>
              <tbody>
                {summary && (
                  <tr style={{ borderBottom: '2px solid #000' }}>
                    <td style={totalStyle}>Total ({rows.length})</td>
                    <td style={totalNum}>{rows.reduce((s, r) => s + (r.hours || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                    <td style={totalNum}>{rows.reduce((s, r) => s + (r.jobs || 0), 0).toLocaleString()}</td>
                    <td style={totalNum}>{(() => { const h = rows.reduce((s, r) => s + (r.hours || 0), 0), j = rows.reduce((s, r) => s + (r.jobs || 0), 0); return h > 0 ? Math.round((j / h) * 10) / 10 : 0; })()}</td>
                    <td style={totalNum}></td>
                    <td style={totalNum}></td>
                    <td style={totalNum}>{quarter === 'all' ? rows.reduce((s, r) => s + (breakGaps[r.empId] || 0), 0) : ''}</td>
                  </tr>
                )}
                <tr style={{ borderBottom: '2px solid #000' }}>
                  <td style={totalStyle}>Total (without managers) ({worked.length})</td>
                  <td style={totalNum}>{wmHours.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                  <td style={totalNum}>{wmJobs.toLocaleString()}</td>
                  <td style={totalNum}>{wmCplh}</td>
                  <td style={totalNum}></td>
                  <td style={totalNum}></td>
                  <td style={totalNum}>{worked.reduce((s, r) => s + (breakGaps[r.empId] || 0), 0)}</td>
                </tr>
                {rows.map(r => {
                  // Highlight: logged real time (>0.5h / 30 min) but running slow (CPLH < 135).
                  const slow = (r.hours || 0) > 0.5 && (r.cplh || 0) < 135;
                  const open = expandedEmp === r.empId;
                  return (
                    <React.Fragment key={r.empId}>
                    <tr style={slow ? { background: '#fdecea' } : undefined}>
                      <td style={{ ...cell, color: slow ? '#c0392b' : undefined, fontWeight: slow ? 700 : 400, cursor: 'pointer', userSelect: 'none' }} onClick={() => setExpandedEmp(open ? null : r.empId)}>
                        <span style={{ color: '#888', marginRight: 4 }}>{open ? '▾' : '▸'}</span>{r.name}
                      </td>
                      <td style={num}>{r.hours.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                      <td style={num}>{r.jobs.toLocaleString()}</td>
                      <td style={{ ...num, fontWeight: 600, color: slow ? '#c0392b' : undefined }}>{r.cplh}</td>
                      <td style={num}>{scanTimes[r.empId]?.first || ''}</td>
                      <td style={num}>{scanTimes[r.empId]?.last || ''}</td>
                      <td style={{ ...num, fontWeight: (breakGaps[r.empId] || 0) > 0 ? 600 : 400, color: (breakGaps[r.empId] || 0) >= 3 ? '#c0392b' : undefined }}>{quarter === 'all' ? (breakGaps[r.empId] || 0) : ''}</td>
                    </tr>
                    {open && (() => {
                      const tl = buildTimeline(r.empId);
                      return (
                        <tr><td colSpan={7} style={{ ...cell, background: '#fafafa', padding: '8px 10px' }}>
                          {!tl ? <span style={{ fontSize: '0.7rem', color: '#888' }}>No timeline data (no scans / punches for this shift yet).</span> : tl.noScans ? <span style={{ fontSize: '0.7rem', color: '#f57f17' }}>This associate has jobs in the rollup but no per-scan detail in the RSR scan feed this shift — can't draw a timeline. (The scan scrape didn't capture their scans.)</span> : (() => {
                            const span = tl.end - tl.sos || 1;
                            const pct = (ms) => `${Math.max(0, Math.min(100, (ms - tl.sos) / span * 100))}%`;
                            const wpct = (s, e) => `${Math.max(0, (e - s) / span * 100)}%`;
                            const col = { scan: '#8e44ad', idle: '#aed8f5', break: '#bdbdbd' };
                            const fmt = (ms) => new Date(ms).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit', hour12: false });
                            return (
                              <div>
                                <div style={{ display: 'flex', gap: 12, fontSize: '0.62rem', color: '#555', marginBottom: 4 }}>
                                  <span><span style={{ display: 'inline-block', width: 10, height: 10, background: col.scan, marginRight: 3 }} />Scanning</span>
                                  <span><span style={{ display: 'inline-block', width: 10, height: 10, background: col.idle, marginRight: 3 }} />Idle ≥5min</span>
                                  <span><span style={{ display: 'inline-block', width: 10, height: 10, background: col.break, marginRight: 3 }} />Break/Lunch</span>
                                  <span style={{ marginLeft: 'auto' }}>{tl.scanCount} scans · {Math.round(tl.idleMin)} idle min · {fmt(tl.sos)}–{fmt(tl.end)}</span>
                                </div>
                                <div style={{ position: 'relative', height: 22, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
                                  {tl.segs.map((s, i) => <div key={i} style={{ position: 'absolute', left: pct(s.startMs), width: wpct(s.startMs, s.endMs), top: 0, bottom: 0, background: col[s.type] }} />)}
                                </div>
                                <div style={{ position: 'relative', height: 26, marginTop: 1 }}>
                                  {tl.markers.map((m, i) => (
                                    <div key={i} style={{ position: 'absolute', left: pct(m.ms), transform: 'translateX(-50%)', textAlign: 'center' }}>
                                      <div style={{ width: 1, height: 5, background: '#555', margin: '0 auto' }} />
                                      <div style={{ fontSize: '0.54rem', color: '#555', whiteSpace: 'nowrap' }}>{m.label}<br />{fmt(m.ms)}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </td></tr>
                      );
                    })()}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Pallet count by destination (from Rodeo VIRTUALLY_LABELED_CASE/PALLET rows) */}
      {/* Current Cage Count + Per-ARC + Per-Cage (from Rodeo cages) */}
      <div className="card" style={{ marginTop: 10 }}>
        <h3 style={{ fontSize: '0.95rem', margin: '0 0 6px' }}>Current Cage Count
          {cages?.generatedAt && <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#888' }}>  · Rodeo {new Date(cages.generatedAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
        </h3>
        {!cageAnalysis ? (
          <p style={{ fontSize: '0.78rem', color: '#888' }}>No Rodeo cage data yet. Run the IDRT Rodeo userscript (it pushes cages grouped by Outer Container).</p>
        ) : (() => {
          const box = { border: '1px solid #ccc', borderRadius: 4, padding: '6px 10px', textAlign: 'center', minWidth: 80 };
          const n = (v) => (v || 0).toLocaleString();
          const chdr = { background: '#232f3e', color: '#fff', border: '1px solid #ccc', padding: '2px 8px', fontSize: '0.68rem', textAlign: 'left', fontWeight: 600 };
          const ccell = { border: '1px solid #eee', padding: '2px 8px', fontSize: '0.72rem' };
          const cnum = { ...ccell, textAlign: 'right' };
          return (
            <div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, fontSize: '0.72rem' }}>
                <div style={{ ...box, background: '#eafaf1' }}><div style={{ color: '#888' }}>Loaded</div><div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1b5e20' }}>{n(cageAnalysis.loaded)}</div></div>
                <div style={{ ...box, background: '#fdecea' }}><div style={{ color: '#888' }}>Empty</div><div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#c0392b' }}>{n(cageAnalysis.empty)}</div></div>
                <div style={{ ...box }}><div style={{ color: '#888' }}>Max</div><div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{n(CAGE_MAX)}</div></div>
                <div style={{ ...box, background: '#fff3e0' }}><div style={{ color: '#888' }}>Mixed</div><div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#e67e22' }}>{n(cageAnalysis.mixed)}</div></div>
              </div>

              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {/* Per Destination (ARC) */}
                <div>
                  <h4 style={{ fontSize: '0.82rem', margin: '0 0 4px' }}>Per Destination (ARC)</h4>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead><tr><th style={chdr}>Dest</th><th style={chdr}>Cages</th><th style={chdr}>Cartons</th><th style={chdr}>ARC %</th><th style={chdr}>Loading</th></tr></thead>
                    <tbody>
                      {cageAnalysis.arcRows.map(r => {
                        const pct = arcPct[r.dest];
                        const ld = arcLoad[r.dest] || { inProgress: 0, scheduled: 0 };
                        return (
                          <tr key={r.dest}>
                            <td style={{ ...ccell, fontWeight: 600 }}>{r.dest}</td>
                            <td style={cnum}>{r.cages}</td>
                            <td style={cnum}>{r.cartons.toLocaleString()}</td>
                            <td style={cnum}>{pct != null ? `${pct}%` : ''}</td>
                            <td style={{ ...ccell, fontWeight: 600, color: ld.inProgress ? '#2e7d32' : '#999' }}>
                              {ld.inProgress ? `▶ ${ld.inProgress} loading` : ''}{ld.scheduled ? <span style={{ color: '#888', fontWeight: 400 }}>{ld.inProgress ? ' · ' : ''}{ld.scheduled} sched</span> : ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Per Cage — Contents Match? */}
                <div style={{ flex: 1, minWidth: 360 }}>
                  <h4 style={{ fontSize: '0.82rem', margin: '0 0 4px' }}>Per Cage — Contents Match?</h4>
                  <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                      <thead><tr><th style={chdr}>Cage</th><th style={chdr}>Children</th><th style={chdr}>Dest(s)</th><th style={chdr}>Match</th></tr></thead>
                      <tbody>
                        {cageAnalysis.perCage.map(c => (
                          <tr key={c.cage} style={{ background: c.destCount > 1 ? '#fdecea' : 'transparent' }}>
                            <td style={ccell}>{c.cage}</td>
                            <td style={{ ...ccell, textAlign: 'right' }}>{c.childCount}</td>
                            <td style={ccell}>{Object.keys(c.dests).map(d => `${d}×${c.dests[d]}`).join(', ') || <span style={{ color: '#fb8c00' }}>unknown</span>}</td>
                            <td style={{ ...ccell, fontWeight: 600, color: c.destCount > 1 ? '#e53935' : (c.destCount === 1 ? '#2e7d32' : '#999') }}>
                              {c.miss && <span style={{ fontWeight: 600, color: '#6a1b9a', marginRight: 6 }}>{c.miss.name || 'unknown'} ({c.miss.csx}){c.miss.time ? ` @ ${c.miss.time}` : ''}</span>}
                              {c.destCount > 1 ? 'MIXED' : (c.destCount === 1 ? 'OK' : '—')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ fontSize: '0.62rem', color: '#999', marginTop: 2 }}>Red = contents span more than one destination (after dropping ghosts). Purple = the single miss-scanned CSX + who scanned it (RSR). A cage with &lt;{MIN_LOADED} items counts as empty. "Ghost" = a minority item (≤3) dwelling &gt;{GHOST_GAP_HRS}h longer than the bulk — treated as a leftover from a prior fill and ignored{cageAnalysis.ghostsDropped ? ` (${cageAnalysis.ghostsDropped} dropped this pull)` : ''}. Empty = {CAGE_MAX} − loaded.</p>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* RSR Support CSX lookup — who scanned a CSX, when (last ~3 days of shifts) */}
      <div className="card" style={{ marginTop: 10 }}>
        <h3 style={{ fontSize: '0.95rem', margin: '0 0 2px' }}>
          RSR Support — Who Scanned This CSX?
          {rsr?.summary && <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#888' }}>  · {rsr.summary.associates} assoc · {(rsr.summary.totalScans || 0).toLocaleString()} scans this shift{rsr.fetched_at ? ` · pulled ${new Date(rsr.fetched_at).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit' })}` : ''}</span>}
        </h3>
        <p style={{ fontSize: '0.62rem', color: '#999', margin: '0 0 8px' }}>Searches the last ~3 days of RSR scan history. Updates hourly from FCLM activity details.</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <input value={rsrCsx} onChange={e => setRsrCsx(e.target.value)} placeholder="Paste / scan a CSX — who scanned it?"
            style={{ fontSize: '0.78rem', padding: '5px 8px', width: 300, border: '1px solid #ccc', borderRadius: 4 }} />
          {rsrCsx.trim() && (() => {
            const hits = rsrHistory[rsrCsx.trim()] || [];
            return <span style={{ fontSize: '0.72rem', color: hits.length ? '#1565c0' : '#999' }}>{hits.length ? `${hits.length} scan(s)` : 'not found in the last ~3 days (or scanned by a non-RSR path)'}</span>;
          })()}
        </div>
        {rsrCsx.trim() && (rsrHistory[rsrCsx.trim()] || []).length > 0 && (
          <table style={{ borderCollapse: 'collapse' }}>
            <thead><tr>{['Date', 'Shift', 'Time', 'Scanned By', 'Emp ID', 'Action'].map(h2 => <th key={h2} style={hdr}>{h2}</th>)}</tr></thead>
            <tbody>
              {[...(rsrHistory[rsrCsx.trim()])]
                .sort((a, b) => String(b.time).localeCompare(String(a.time)))
                .map((e, i) => (
                  <tr key={i}>
                    <td style={cell}>{e.date}</td>
                    <td style={cell}>{e.shift}</td>
                    <td style={cell}>{clock(e.time)}</td>
                    <td style={{ ...cell, fontWeight: 600 }}>{e.name}</td>
                    <td style={cell}>{e.empId}</td>
                    <td style={cell}>{e.action}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Miss-scans this shift — grouped by associate (single-miss cages) */}
      {cageAnalysis?.missByAssoc && (
        <div className="card" style={{ marginTop: 10 }}>
          <h3 style={{ fontSize: '0.95rem', margin: '0 0 2px' }}>
            Miss-scans This Shift
            <span style={{ fontWeight: 400, fontSize: '0.68rem', color: '#888' }}>  · {cageAnalysis.missByAssoc.length} associate(s) · {cageAnalysis.missByAssoc.reduce((s, a) => s + a.count, 0)} miss-scan(s)</span>
          </h3>
          <p style={{ fontSize: '0.62rem', color: '#999', margin: '0 0 8px' }}>Single-miss cages whose outlier CSX was scanned by an RSR associate this shift. Expand a name to see the cages/CSX/time.</p>
          {cageAnalysis.missByAssoc.length === 0 ? (
            <p style={{ fontSize: '0.78rem', color: '#888' }}>No attributable miss-scans this shift.</p>
          ) : (
            <div style={{ fontSize: '0.74rem' }}>
              {cageAnalysis.missByAssoc.map(a => (
                <details key={a.name} style={{ borderBottom: '1px solid #eee', padding: '3px 0' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                    {a.name} <span style={{ color: '#6a1b9a' }}>({a.count})</span>{a.empId ? <span style={{ color: '#999', fontWeight: 400 }}> · {a.empId}</span> : null}
                  </summary>
                  <table style={{ borderCollapse: 'collapse', margin: '4px 0 6px 14px' }}>
                    <thead><tr>{['Cage', 'CSX', 'Wrong ARC', 'Time'].map(h2 => <th key={h2} style={hdr}>{h2}</th>)}</tr></thead>
                    <tbody>
                      {a.scans.map((s, i) => (
                        <tr key={i}>
                          <td style={cell}>{s.cage}</td>
                          <td style={cell}>{s.csx}</td>
                          <td style={{ ...cell, fontWeight: 600 }}>{s.dest}</td>
                          <td style={cell}>{s.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Pallets by Destination (from Rodeo VIRTUALLY_LABELED_CASE/PALLET rows,
          >160h dwell dropped upstream). Compact table at the very bottom. */}
      {pallets && pallets.byDest.length > 0 && (() => {
        const tc = { border: '1px solid #ccc', padding: '1px 4px', fontSize: '0.7rem', lineHeight: 1.2 };
        const th = { ...tc, background: '#232f3e', color: '#fff' };
        return (
          <div className="card" style={{ marginTop: 10, display: 'inline-block' }}>
            <h3 style={{ fontSize: '0.85rem', margin: '0 0 4px' }}>Pallets by Destination
              {pallets.generatedAt && <span style={{ fontWeight: 400, fontSize: '0.62rem', color: '#888' }}>  · Rodeo {new Date(pallets.generatedAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
            </h3>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Dest</th>
                  <th style={{ ...th, textAlign: 'right' }}>Pallets</th>
                  <th style={{ ...th, textAlign: 'right' }}>Cases</th>
                  <th style={{ ...th, textAlign: 'right' }}>CPP</th>
                </tr>
              </thead>
              <tbody>
                {pallets.byDest.map(d => (
                  <tr key={d.destination} style={d.stale ? { color: '#999' } : undefined}
                      title={d.stale ? `Held from last good run${d.updatedAt ? ' · ' + new Date(d.updatedAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''} (this ARC was missing from the latest Rodeo pull)` : undefined}>
                    <td style={tc}>{d.destination}{d.stale ? <span style={{ color: '#f0ad4e', marginLeft: 3 }} title="held from last good run">◷</span> : null}</td>
                    <td style={{ ...tc, textAlign: 'right' }}>{d.pallets}</td>
                    <td style={{ ...tc, textAlign: 'right' }}>{d.cases.toLocaleString()}</td>
                    <td style={{ ...tc, textAlign: 'right' }}>{d.pallets ? (d.cases / d.pallets).toFixed(1) : ''}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, background: '#eef2f7' }}>
                  <td style={tc}>Total</td>
                  <td style={{ ...tc, textAlign: 'right' }}>{pallets.totalPallets}</td>
                  <td style={{ ...tc, textAlign: 'right' }}>{pallets.totalCases.toLocaleString()}</td>
                  <td style={{ ...tc, textAlign: 'right' }}>{pallets.totalPallets ? (pallets.totalCases / pallets.totalPallets).toFixed(1) : ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}
